<template>
  <div class="week-page">
    <Header></Header>
    <div class="col-12 d-inline-flex">
      <div class="col-10 no-overflow ms-3 me-3 pt-3" style="position: relative;">
        <div class="week-heading">
          <h2>Week</h2>
          <span class="week-subtitle">
            All leagues, your local time ({{ timezoneLabel }}). Click a day to open Daily, or a match to jump to it there.
          </span>
        </div>

        <div class="week-toolbar">
          <div class="week-nav">
            <button type="button" class="week-nav-btn" title="Previous week" aria-label="Previous week" @click="goPrevWeek"><svg class="wk-chev" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path d="M10 3L5 8l5 5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg></button>
            <div class="week-title">{{ weekLabelText }}</div>
            <button type="button" class="week-nav-btn" title="Next week" aria-label="Next week" @click="goNextWeek"><svg class="wk-chev" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path d="M6 3l5 5-5 5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg></button>
          </div>
          <button type="button" class="week-today-btn" :disabled="isCurrentWeek" @click="goToday">Today</button>
          <div class="week-legend">
            <span v-for="lg in leagueLegend" :key="lg.calLeagueId" class="week-legend-item">
              <span class="week-legend-swatch" :class="seriesClass(lg.calLeagueId)"></span>{{ lg.name }}
            </span>
            <span class="week-legend-item text-muted"><span class="week-legend-swatch wk-inactive-swatch"></span>Inactive</span>
            <span class="week-legend-item text-muted" title="Active match with no events assigned"><span class="wk-setup-dot">!</span>No events</span>
            <span class="week-legend-item text-muted" title="Source conflict or event double-booked"><span class="wk-conflict-dot">!</span>Conflict</span>
          </div>
        </div>

        <div v-if="loadError" class="error-message mb-3">{{ loadError }}</div>

        <div class="week-cal" :class="{ 'week-cal--loading': loading, 'week-cal--ready': ready }">
          <div class="week-head">
            <div class="week-gutter"></div>
            <div
                v-for="day in days" :key="day.param"
                class="week-day-head"
                :class="{ 'is-today': day.isToday }"
                role="button"
                :title="'Open Daily for ' + day.longLabel"
                @click="openDay(day.param)"
            >
              <span class="week-day-name">{{ day.weekday }}</span>
              <span class="week-day-num">{{ day.dayNum }}</span>
              <span class="week-day-count">{{ countLabel(matchCountByDay[day.param]) }}</span>
            </div>
          </div>

          <div class="week-allday" :style="{ gridTemplateRows: 'repeat(' + Math.max(allDayLaneCount, 1) + ', 24px)' }">
            <div class="week-gutter week-allday-label" :style="{ gridRow: '1 / span ' + Math.max(allDayLaneCount, 1) }">Multi-day</div>
            <div
                v-for="bar in allDayBars" :key="'ad-' + bar.match.calMatchId"
                class="week-allday-bar"
                :class="[seriesClass(bar.match.calLeagueId), {
                  'is-inactive': !bar.match.active,
                  'cont-left': bar.contLeft,
                  'cont-right': bar.contRight,
                  'has-conflict': hasConflict(bar.match)
                }]"
                :style="{ gridColumn: (bar.startCol + 2) + ' / ' + (bar.endCol + 3), gridRow: bar.lane + 1 }"
                role="button"
                :title="matchTooltip(bar.match)"
                @click="openMatch(bar.match, bar.openDate)"
            >
              <span v-if="hasConflict(bar.match)" class="wk-conflict-dot">!</span>
              <span v-else-if="needsSetup(bar.match)" class="wk-setup-dot">!</span>
              <b>{{ bar.match.team1 }} vs {{ bar.match.team2 }}</b>
              <span class="week-allday-meta">{{ leagueShort(bar.match.calLeagueId) }} &middot; {{ formatDateTime(bar.match.startTime) }} &rarr; {{ formatDateTime(effectiveEnd(bar.match)) }}</span>
              <span class="week-allday-meta">&middot; {{ setupCountsText(bar.match) }}<template v-if="appsText(bar.match)"> &middot; {{ appsText(bar.match) }}</template></span>
            </div>
          </div>

          <div ref="scroller" class="week-scroll">
            <div class="week-grid" :style="{ height: (24 * hourPx) + 'px' }">
              <div class="week-gutter week-hours">
                <div v-for="h in 24" :key="h" class="week-hour-label" :style="{ top: ((h - 1) * hourPx) + 'px' }">
                  {{ h === 1 ? '' : hourLabel(h - 1) }}
                </div>
              </div>
              <div
                  v-for="day in days" :key="'col-' + day.param"
                  class="week-day-col"
                  :class="{ 'is-today': day.isToday }"
                  role="button"
                  :title="'Open Daily for ' + day.longLabel"
                  @click="openDay(day.param)"
              >
                <div v-for="h in 24" :key="h" class="week-hour-line" :style="{ top: ((h - 1) * hourPx) + 'px' }"></div>
                <div
                    v-for="seg in segmentsByDay[day.param]" :key="'seg-' + seg.match.calMatchId"
                    class="week-event"
                    :class="[seriesClass(seg.match.calLeagueId), {
                      'is-inactive': !seg.match.active,
                      'is-short': seg.heightPx < 38,
                      'cont-top': seg.contTop,
                      'cont-bottom': seg.contBottom,
                      'has-conflict': hasConflict(seg.match)
                    }]"
                    :style="segStyle(seg, day.param)"
                    :title="matchTooltip(seg.match)"
                    @click.stop="openMatch(seg.match, day.param)"
                    @mouseenter="hovered = { day: day.param, cluster: seg.cluster, col: seg.col }"
                    @mouseleave="hovered = null"
                >
                  <div class="week-event-title">
                    <span>{{ seg.match.team1 }}</span><span>vs {{ seg.match.team2 }}</span>
                  </div>
                  <div class="week-event-meta">
                    {{ formatTime(seg.match.startTime) }} &ndash; {{ formatTime(effectiveEnd(seg.match)) }}
                    &middot; {{ leagueShort(seg.match.calLeagueId) }}
                  </div>
                  <div class="week-event-setup">{{ setupCountsText(seg.match) }}</div>
                  <div v-if="appsText(seg.match)" class="week-event-setup">{{ appsText(seg.match) }}</div>
                  <div v-if="hasConflict(seg.match)" class="week-event-flag">
                    <span class="wk-conflict-dot">!</span><span class="week-event-flag-label">Conflict</span>
                  </div>
                  <div v-else-if="needsSetup(seg.match)" class="week-event-flag">
                    <span class="wk-setup-dot">!</span><span class="week-event-flag-label">No events</span>
                  </div>
                </div>
                <div v-if="day.isToday && nowTopPx !== null" class="week-now-line" :style="{ top: nowTopPx + 'px' }"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AdminService from "@/services/admin-service";
import Header from "@/components/Header.vue";
import { CalMatchPageFilter, CalMatchPageRequest } from "@/model/models";

const DAY_MS = 24 * 60 * 60 * 1000;
const HOUR_PX = 48;
// Visual floor for very short (or endTime-less) matches, also used when packing overlaps.
const MIN_EVENT_MINUTES = 30;
const SERIES_COUNT = 8;
const PAGE_SIZE = 100;
const HOVER_WIDTH = 0.7;

function toDateParam(d) {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

function isValidDateParam(value) {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

// Local midnight of the Monday on or before the given local date. Built with the
// (y, m, d) constructor so DST days don't shift the boundary.
function mondayOf(date) {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d;
}

function minutesIntoDay(date, dayStart, dayEnd) {
  if (date <= dayStart) return 0;
  if (date >= dayEnd) return 24 * 60;
  return date.getHours() * 60 + date.getMinutes();
}

export default {
  name: "WeekCalendar",
  components: { Header },
  data() {
    const param = isValidDateParam(this.$route.query.date) ? this.$route.query.date : toDateParam(new Date());
    return {
      hourPx: HOUR_PX,
      weekStart: mondayOf(new Date(`${param}T00:00:00`)),
      calLeaguesList: [],
      calMatchesList: [],
      // { [calMatchId]: CalMatchSetupSummary } from list-paged.
      setupById: {},
      // { day, cluster, col } of the hovered block - its cluster re-lays out so it opens wide.
      hovered: null,
      loading: false,
      // Grid stays hidden until matches are placed and the scroll position is set, then fades in.
      ready: false,
      loadError: '',
      now: new Date(),
      nowTimer: null
    };
  },
  computed: {
    days() {
      const todayParam = toDateParam(this.now);
      return Array.from({ length: 7 }, (_, i) => {
        const start = new Date(this.weekStart.getFullYear(), this.weekStart.getMonth(), this.weekStart.getDate() + i);
        const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 1);
        const param = toDateParam(start);
        return {
          index: i,
          start,
          end,
          param,
          isToday: param === todayParam,
          weekday: start.toLocaleDateString(undefined, { weekday: 'short' }),
          dayNum: start.getDate(),
          longLabel: start.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })
        };
      });
    },
    weekEnd() {
      return this.days[6].end;
    },
    isCurrentWeek() {
      return this.days.some(d => d.isToday);
    },
    weekLabelText() {
      const first = this.days[0].start;
      const last = this.days[6].start;
      const sameYear = first.getFullYear() === last.getFullYear();
      const left = first.toLocaleDateString(undefined, sameYear
          ? { day: 'numeric', month: 'short' }
          : { day: 'numeric', month: 'short', year: 'numeric' });
      const right = last.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
      return `${left} – ${right}`;
    },
    timezoneLabel() {
      const offsetMin = -new Date().getTimezoneOffset();
      const sign = offsetMin >= 0 ? '+' : '-';
      const abs = Math.abs(offsetMin);
      return `UTC${sign}${String(Math.floor(abs / 60)).padStart(2, '0')}:${String(abs % 60).padStart(2, '0')}`;
    },
    calLeaguesById() {
      const map = {};
      for (const lg of this.calLeaguesList) map[lg.calLeagueId] = lg;
      return map;
    },
    weekMatches() {
      return this.calMatchesList
          .filter(m => this.touches(m, this.weekStart, this.weekEnd))
          .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
    },
    // Leagues with a match this week, by name - each gets a fixed color for the week.
    leagueLegend() {
      const ids = [...new Set(this.weekMatches.map(m => m.calLeagueId))];
      return ids
          .map(id => ({ calLeagueId: id, name: this.calLeaguesById[id]?.name || `League ${id}` }))
          .sort((a, b) => a.name.localeCompare(b.name));
    },
    leagueSeriesById() {
      const map = {};
      this.leagueLegend.forEach((lg, i) => { map[lg.calLeagueId] = i < SERIES_COUNT ? i + 1 : null; });
      return map;
    },
    matchCountByDay() {
      const map = {};
      for (const day of this.days) {
        map[day.param] = this.weekMatches.filter(m => this.touches(m, day.start, day.end)).length;
      }
      return map;
    },
    // Matches of 24h+ go in the multi-day row (like Google's all-day events); everything
    // else is drawn in the time grid, split per day if it crosses midnight.
    longMatches() {
      return this.weekMatches.filter(m => this.durationMs(m) >= DAY_MS);
    },
    allDayBars() {
      const laneEnds = [];
      return this.longMatches.map(match => {
        const start = new Date(match.startTime);
        const end = new Date(this.effectiveEnd(match));
        const startCol = Math.max(0, this.days.findIndex(d => start < d.end));
        let endCol = 6;
        for (let i = 6; i >= 0; i--) {
          if (end > this.days[i].start) { endCol = i; break; }
        }
        let lane = laneEnds.findIndex(lastCol => lastCol < startCol);
        if (lane === -1) { lane = laneEnds.length; laneEnds.push(endCol); } else { laneEnds[lane] = endCol; }
        return {
          match,
          startCol,
          endCol,
          lane,
          contLeft: start < this.weekStart,
          contRight: end > this.weekEnd,
          openDate: start < this.weekStart ? this.days[0].param : toDateParam(start)
        };
      });
    },
    allDayLaneCount() {
      return this.allDayBars.reduce((max, b) => Math.max(max, b.lane + 1), 0);
    },
    // { [dateParam]: [{ match, topPx, heightPx, col, cols, contTop, contBottom }] }
    segmentsByDay() {
      const out = {};
      const timed = this.weekMatches.filter(m => this.durationMs(m) < DAY_MS);
      for (const day of this.days) {
        const segs = timed
            .filter(m => this.touches(m, day.start, day.end))
            .map(match => {
              const start = new Date(match.startTime);
              const end = new Date(this.effectiveEnd(match));
              const startMin = minutesIntoDay(start, day.start, day.end);
              const endMin = Math.max(minutesIntoDay(end, day.start, day.end), startMin);
              return {
                match,
                startMin,
                visualEndMin: Math.min(24 * 60, Math.max(endMin, startMin + MIN_EVENT_MINUTES)),
                contTop: start < day.start,
                contBottom: end > day.end
              };
            });
        out[day.param] = this.packOverlaps(segs).map(s => ({
          ...s,
          topPx: s.startMin / 60 * HOUR_PX,
          heightPx: Math.max((s.visualEndMin - s.startMin) / 60 * HOUR_PX - 2, 18)
        }));
      }
      return out;
    },
    nowTopPx() {
      return (this.now.getHours() * 60 + this.now.getMinutes()) / 60 * HOUR_PX;
    }
  },
  created() {
    this.load();
  },
  mounted() {
    this.nowTimer = setInterval(() => { this.now = new Date(); }, 60 * 1000);
  },
  beforeUnmount() {
    clearInterval(this.nowTimer);
  },
  methods: {
    load() {
      this.loading = true;
      this.loadError = '';
      // list-paged's window is an overlap filter, so multi-day matches that started before
      // the week are included without extra slack.
      const filter = new CalMatchPageFilter(this.weekStart.toISOString(), this.weekEnd.toISOString());
      Promise.all([
        AdminService.getCalLeaguesList(true),
        this.fetchAllPages(filter)
      ]).then(([leagues, pages]) => {
        this.calLeaguesList = leagues || [];
        this.calMatchesList = pages.flatMap(p => p.calMatchList || []);
        const setupById = {};
        pages.flatMap(p => p.setupSummaries || []).forEach(s => { setupById[s.calMatchId] = s; });
        this.setupById = setupById;
        this.$nextTick(() => {
          this.scrollToFirstMatch();
          this.ready = true;
        });
      }).catch((error) => {
        console.error("Error loading week data:", error);
        this.loadError = this.extractErrorMessage(error);
        this.ready = true;
      }).finally(() => {
        this.loading = false;
      });
    },
    // The BE caps a page at 100 matches - keep requesting until the whole week is in.
    fetchAllPages(filter, page = 0, acc = []) {
      return AdminService.getCalMatchesPaged(new CalMatchPageRequest(page, PAGE_SIZE, undefined, filter)).then((res) => {
        const pages = [...acc, res];
        const loaded = pages.reduce((n, p) => n + (p.calMatchList || []).length, 0);
        const more = (res.calMatchList || []).length === PAGE_SIZE && loaded < res.totalElements;
        return more ? this.fetchAllPages(filter, page + 1, pages) : pages;
      });
    },
    // Classic calendar layout: overlapping segments form a cluster, each gets the first free
    // column, and every segment in the cluster shares the cluster's column count.
    packOverlaps(segs) {
      const sorted = segs.slice().sort((a, b) => a.startMin - b.startMin || b.visualEndMin - a.visualEndMin);
      const result = [];
      let cluster = [];
      let columns = [];
      let clusterEnd = -1;
      let clusterIndex = 0;
      const flush = () => {
        cluster.forEach(s => { s.cols = columns.length; s.cluster = clusterIndex; });
        clusterIndex++;
        result.push(...cluster);
        cluster = [];
        columns = [];
      };
      for (const seg of sorted) {
        if (cluster.length && seg.startMin >= clusterEnd) flush();
        let col = columns.findIndex(colEnd => colEnd <= seg.startMin);
        if (col === -1) { col = columns.length; columns.push(seg.visualEndMin); } else { columns[col] = seg.visualEndMin; }
        seg.col = col;
        cluster.push(seg);
        clusterEnd = Math.max(clusterEnd, seg.visualEndMin);
      }
      if (cluster.length) flush();
      return result;
    },
    // Like Google Calendar, side-by-side blocks overlap a little so titles stay readable;
    // the later column is drawn on top.
    columnGeometry(col, cols) {
      const leftPct = col / cols * 100;
      const widthPct = col === cols - 1 ? 100 / cols : Math.min(100 - leftPct, 100 / cols * 1.7);
      return { leftPct, widthPct };
    },
    // Hovered cluster: the hovered column takes most of the width and the others shrink to
    // slivers on either side, so a right-edge block opens to the left. The hovered block
    // always still covers its original spot, so the layout can't flicker under the cursor.
    hoverGeometry(col, cols, hoveredCol) {
      const hoveredPct = HOVER_WIDTH * 100;
      const sliverPct = (100 - hoveredPct) / (cols - 1);
      const leftPct = col <= hoveredCol ? col * sliverPct : col * sliverPct - sliverPct + hoveredPct;
      return { leftPct, widthPct: col === hoveredCol ? hoveredPct : sliverPct };
    },
    segStyle(seg, dayParam) {
      const h = this.hovered;
      const inHoveredCluster = h && seg.cols > 1 && h.day === dayParam && h.cluster === seg.cluster;
      const { leftPct, widthPct } = inHoveredCluster
          ? this.hoverGeometry(seg.col, seg.cols, h.col)
          : this.columnGeometry(seg.col, seg.cols);
      return {
        top: seg.topPx + 'px',
        height: seg.heightPx + 'px',
        left: `calc(${leftPct}% + 1px)`,
        width: `calc(${widthPct}% - 3px)`,
        zIndex: inHoveredCluster && seg.col === h.col ? 20 : 2 + seg.col
      };
    },
    scrollToFirstMatch() {
      const el = this.$refs.scroller;
      if (!el) return;
      const starts = Object.values(this.segmentsByDay).flat().filter(s => !s.contTop).map(s => s.startMin);
      const firstMin = starts.length ? Math.min(...starts) : 8 * 60;
      el.scrollTop = Math.max(0, (Math.min(firstMin, 8 * 60) - 30) / 60 * HOUR_PX);
    },
    effectiveEnd(match) {
      return match.endTime || match.startTime;
    },
    durationMs(match) {
      return new Date(this.effectiveEnd(match)) - new Date(match.startTime);
    },
    touches(match, rangeStart, rangeEnd) {
      if (!match.startTime) return false;
      const start = new Date(match.startTime);
      const end = new Date(this.effectiveEnd(match));
      if (isNaN(start.getTime()) || isNaN(end.getTime())) return false;
      if (end <= start) return start >= rangeStart && start < rangeEnd;
      return start < rangeEnd && end > rangeStart;
    },
    needsSetup(match) {
      const setup = this.setupById[match.calMatchId];
      return match.active && !!setup && setup.eventCount === 0;
    },
    hasConflict(match) {
      const setup = this.setupById[match.calMatchId];
      return !!setup && (setup.channelConflict || setup.eventDoubleBooked);
    },
    setupCountsText(match) {
      const setup = this.setupById[match.calMatchId];
      if (!setup) return '';
      const src = `${setup.channelCount} source${setup.channelCount === 1 ? '' : 's'}`;
      const ev = `${setup.eventCount} event${setup.eventCount === 1 ? '' : 's'}`;
      return `${src} · ${ev}`;
    },
    appsText(match) {
      const setup = this.setupById[match.calMatchId];
      return setup && setup.applications ? setup.applications.join(' · ') : '';
    },
    seriesClass(calLeagueId) {
      const n = this.leagueSeriesById[calLeagueId];
      return n ? `wk-series-${n}` : 'wk-series-overflow';
    },
    leagueShort(calLeagueId) {
      const lg = this.calLeaguesById[calLeagueId];
      return lg ? (lg.shortName || lg.name) : '';
    },
    countLabel(n) {
      if (!n) return 'No matches';
      return `${n} match${n === 1 ? '' : 'es'}`;
    },
    hourLabel(h) {
      return `${String(h).padStart(2, '0')}:00`;
    },
    formatTime(iso) {
      return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
    },
    formatDateTime(iso) {
      return new Date(iso).toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false });
    },
    matchTooltip(match) {
      const lg = this.calLeaguesById[match.calLeagueId];
      const lines = [
        `${match.team1} vs ${match.team2}`,
        `${this.formatDateTime(match.startTime)} → ${this.formatDateTime(this.effectiveEnd(match))}`,
        lg ? lg.name : '',
        match.venue || '',
        match.active ? '' : 'Inactive',
        this.setupCountsText(match),
        this.appsText(match),
        this.setupById[match.calMatchId]?.channelConflict ? 'Source conflict' : '',
        this.setupById[match.calMatchId]?.eventDoubleBooked ? 'Event double-booked' : '',
        this.needsSetup(match) ? 'No events yet - needs setup' : ''
      ];
      return lines.filter(Boolean).join('\n');
    },
    goToWeekOf(date) {
      this.$router.push({ name: 'WeekCalendar', query: { date: toDateParam(date) } });
    },
    goPrevWeek() {
      this.goToWeekOf(new Date(this.weekStart.getFullYear(), this.weekStart.getMonth(), this.weekStart.getDate() - 7));
    },
    goNextWeek() {
      this.goToWeekOf(new Date(this.weekStart.getFullYear(), this.weekStart.getMonth(), this.weekStart.getDate() + 7));
    },
    goToday() {
      this.goToWeekOf(new Date());
    },
    openDay(dateParam) {
      this.$router.push({ name: 'Daily', query: { date: dateParam } });
    },
    openMatch(match, dateParam) {
      this.$router.push({ name: 'Daily', query: { date: dateParam, openMatch: match.calMatchId } });
    },
    extractErrorMessage(error) {
      if (error.response && error.response.data && error.response.data.message) {
        return error.response.data.message;
      }
      return error.message || 'Something went wrong.';
    }
  }
};
</script>
<style src="../../assets/style.css"></style>
